import { CellRange, DataMap } from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { FlexGridFilter } from '@grapecity/wijmo.react.grid.filter';
import TextComponent from 'components/Input/TextComponent';
import { useAuth } from 'context/AuthContext';
import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import ScreenTransitionMenu from 'components/Navigation/ScreenTransitionMenu';
import { FlexGridXlsxConverter } from '@grapecity/wijmo.grid.xlsx';
import SelectComponent from 'components/Input/SelectComponent';

import ConstructionScheduleEditModal from 'views/ConstructionScheduleList/ConstructionScheduleEditModal';

import { ProjectTypes } from 'constants/constants';

// 工事
const ConstructionScheduleList = (props) => {
  const auth = useAuth();
  const restApi = useRestApi();
  const location = useLocation();
  // 履歴
  const navigate = useNavigate();
  // グリッド
  const flexGrid = useRef();

  // 工事一覧
  const [constructionRequests, setConstructionRequests] = useState([]);

  // 選択中の工事No
  const [targetKoujiNo, setTargetKoujiNo] = useState();

  // 一覧の件数
  const [count, setCount] = useState(0);

  // 一覧の全件数
  const [total, setTotal] = useState(0);

  // 工事状況モーダルへのパラメタ
  const [constructionScheduleEditModalParam, setConstructionScheduleEditModalParam] = useState({});

  // 顧客検索条件
  const [searchCondition, setSearchCondition] = useState({
    // 契約日は未入力（全期間）で開始する
    KEI_DATE_START: '',
    KEI_DATE_END: '',
    KEYWORD: '',
  });

  // マスタ情報
  const master = useMaster();

  // 年間管理で絞るかチェック
  const [isAnnualManagement, setIsAnnualManagement] = useState(false);
  // 完了した工事を含めるかチェック
  const [isIncludeCompleted, setIsIncludeCompleted] = useState(false);
  // 顧客区分公共のみで絞るかチェック
  const [isClientTypePublicOnly, setIsClientTypePublicOnly] = useState(false);

  // 検索処理
  const SearchClients = (condition, targetKoujiNo = null) => {
    // 年間管理の条件を追加
    const searchCondition = {
      ...condition,
      IS_ANNUAL_MANAGEMENT: isAnnualManagement,
      IS_INCLUDE_COMPLETED: isIncludeCompleted,
      IS_CLIENT_TYPE_PUBLIC_ONLY: isClientTypePublicOnly,
    };
    restApi.post('/api/constructionScheduleList/list', searchCondition, (data) => {
      const _quoteRequests = data.baseConstruction.map((quoteRequest) => {
        return {
          ...quoteRequest,
          TAN_MEETING_FLG_DISP: quoteRequest.TAN_MEETING_FLG == 1 ? '〇' : '',
          GREETING_FLG_DISP: quoteRequest.GREETING_FLG == 1 ? '〇' : '',
          INSPECTION_FLG_DISP: quoteRequest.INSPECTION_FLG == 1 ? '〇' : '',
        };
      });
      setTotal(data.total);
      setConstructionRequests(_quoteRequests);
      setTargetKoujiNo(targetKoujiNo);
    });
  };

  // 初期表示処理
  useEffect(() => {
    // 別画面から戻ってきた場合は検索条件を復活
    let condition = location?.state?.searchCondition ?? {
      // 契約日は未入力（全期間）で開始する
      KEI_DATE_START: '',
      KEI_DATE_END: '',
      KEYWORD: '',
    };
    setSearchCondition(condition);
    // 検索
    SearchClients(condition, location?.state?.targetClientCd);
  }, []);

  // 他の画面から戻ってきた場合に顧客を選択
  useEffect(() => {
    selectTarget(targetKoujiNo);
  }, [targetKoujiNo]);

  const selectTarget = (targetKoujiNo) => {
    if (targetKoujiNo) {
      const grid = flexGrid.current.control;

      // 対象のSEIKYU_NOを持つ行のインデックスを探す
      const index = grid.rows.findIndex((row) => row.dataItem?.SEIKYU_NO === targetKoujiNo);

      if (index >= 0) {
        setTimeout(() => {
          grid.select(new CellRange(index), true); // 対象行を選択状態にする
        }, 10);
      }
    }
  };

  // コード翻訳用のマッピング
  const planerWithEmpty = [...master.planer, { id: 0, name: '' }];
  const planerMap = new DataMap(planerWithEmpty, 'id', 'name');
  var projectTypesMap = new DataMap(ProjectTypes, 'key', 'value');
  var robotTypesMap = new DataMap(
    [{ ROBOT_TYPE: 0, ROBOT_TYPE_NAME: '' }, ...master.robotTypes],
    'ROBOT_TYPE',
    'ROBOT_TYPE_NAME'
  );

  // 顧客情報ボタン
  const handleOnClickClientInfo = useCallback(() => {
    navigate('/admin/ClientInfo', {
      state: {
        clientId: flexGrid.current.control.selectedItems[0]['CLIENT_CD'],
        seikyuNo: flexGrid.current.control.selectedItems[0]['SEIKYU_NO'],
      },
    });
  }, [navigate]);

  // 検索ボタン
  const handleOnClickSearch = () => {
    SearchClients(searchCondition);
  };

  // 編集処理
  const handleChange = (prop) => (target) => {
    setSearchCondition({ ...searchCondition, [prop]: target?.value ?? target?.target.value });
  };

  // Excelダウンロード
  const handleExcelDownload = () => {
    const grid = flexGrid.current.control; // FlexGridコンポーネントへの参照を取得
    const fileName = '工事工程表.xlsx'; // ダウンロードするExcelファイルの名前

    // Excelファイルを生成し、ダウンロードする
    FlexGridXlsxConverter.save(
      grid,
      {
        includeColumns: function (column) {
          return column.header; // ヘッダー名称なしは含めない（ボタンの項目）
        },
      },
      fileName
    );
  };

  // 工事状況編集ボタン
  const handleOnClickStatusEdit = () => {
    const grid = flexGrid.current.control;
    const collectionView = grid.collectionView;

    // 工事状況編集モーダルの呼び出し処理
    setConstructionScheduleEditModalParam({
      call_list: true,
      constructionData: grid.selectedItems[0], // 現在選択されている行のデータを渡す
      // 工事状況編集後のコールバック関数
      callback: (construction) => {
        // ステータスを数値型に変換
        const updatedQuoteRequest = {
          ...construction,
        };

        // SEIKYU_NOが一致する行を検索
        const target = collectionView.items.find(
          (item) => item.SEIKYU_NO === construction.SEIKYU_NO
        );

        if (target) {
          // 対象行を直接上書き
          Object.assign(target, updatedQuoteRequest);

          //　再描画して変更を反映
          collectionView.refresh();

          // 編集した行を再選択
          selectTarget(construction.SEIKYU_NO);
        }
      },
    });
  };

  // フィルタの変更処理
  const filterApplied = (s, e) => {
    let grid = s.grid;
    setCount(grid?.collectionView?.items.length);
  };

  // フォームのsubmit処理
  const handleOnSubmitSearch = (e) => {
    e.preventDefault();
    handleOnClickSearch();
  };

  return (
    <>
      <Card className="m-0 table-container">
        <Card.Body>
          <Row>
            <Col>
              <form onSubmit={handleOnSubmitSearch}>
                <Row>
                  <Col className="pl-3 pr-2" xs="auto" style={{ width: '300px' }}>
                    <Form.Group>
                      <label>絞り込み</label>
                      <TextComponent
                        placeholder="顧客名・住所・電話番号"
                        type="text"
                        value={searchCondition.KEYWORD}
                        onChange={handleChange('KEYWORD')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="2.5">
                    <Form.Group>
                      <label>契約日</label>
                      <div className="d-flex" style={{ width: '340px' }}>
                        <TextComponent
                          type="date"
                          value={searchCondition.KEI_DATE_START}
                          onChange={handleChange('KEI_DATE_START')}
                        ></TextComponent>
                        <span className="mt-2 ml-2 mr-2">～</span>
                        <TextComponent
                          type="date"
                          value={searchCondition.KEI_DATE_END}
                          onChange={handleChange('KEI_DATE_END')}
                        ></TextComponent>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="1.5" style={{ width: '200px' }}>
                    <Form.Group>
                      <label>営業担当</label>
                      <SelectComponent
                        value={searchCondition.SALES_TAN_CD}
                        onChange={handleChange('SALES_TAN_CD')}
                      >
                        <option value={''}></option>
                        {master.salesStaff.map((x, i) => (
                          <option key={i} value={x['id']}>
                            {x['name']}
                          </option>
                        ))}
                      </SelectComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="1.5" style={{ width: '200px' }}>
                    <Form.Group>
                      <label>現場担当</label>
                      <SelectComponent
                        value={searchCondition.GENBA_TAN_CD}
                        onChange={handleChange('GENBA_TAN_CD')}
                      >
                        <option value={''}></option>
                        {master.genbaStaff.map((x, i) => (
                          <option key={i} value={x['id']}>
                            {x['name']}
                          </option>
                        ))}
                      </SelectComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pl-2 pr-2" md="1.5">
                    <Form.Group>
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isAnnualManagement}
                          onChange={() => setIsAnnualManagement(!isAnnualManagement)}
                        ></input>
                        年間管理(個人・法人)除外
                      </label>
                      <br />
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isIncludeCompleted}
                          onChange={() => setIsIncludeCompleted(!isIncludeCompleted)}
                        ></input>
                        完了・失注の工事を含める
                      </label>
                      <br />
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isClientTypePublicOnly}
                          onChange={() => setIsClientTypePublicOnly(!isClientTypePublicOnly)}
                        ></input>
                        公共で絞る
                      </label>
                    </Form.Group>
                  </Col>
                  <Col className="mt-1">
                    <Form.Group>
                      <br />
                      <Button
                        variant="success"
                        className="btn-fill"
                        size="sm"
                        type="submit"
                      >
                        検索
                      </Button>
                      {/* 入力中の検索条件を引き継いで各一覧へ遷移する */}
                      <ScreenTransitionMenu
                        current="constructionSchedule"
                        searchCondition={searchCondition}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </form>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                variant="success"
                className="btn-fill m-2"
                size="sm"
                onClick={handleExcelDownload}
              >
                Excelダウンロード
              </Button>
              件数：{count}件 / 全{total}件
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <div className="table-scroll" style={{ height: 'calc(100vh - 290px)' }}>
                <FlexGrid
                  ref={flexGrid}
                  isReadOnly={true}
                  itemsSource={constructionRequests}
                  selectionMode={'Row'}
                  headersVisibility={'Column'}
                  style={{ height: '100%' }}
                  frozenColumns={6}
                  loadedRows={(grid) => {
                    grid.rows.forEach((ele) => {
                      ele.height = 38;
                    });
                  }}
                >
                  <FlexGridFilter filterApplied={filterApplied} />
                  <FlexGridColumn header="" binding="" isReadOnly={true} width={84}>
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="success"
                            title="顧客情報"
                            onClick={handleOnClickClientInfo}
                          >
                            person
                          </Button>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="dark"
                            title="工事状況編集"
                            onClick={handleOnClickStatusEdit}
                          >
                            more_horiz
                          </Button>
                        </span>
                      )}
                    />
                  </FlexGridColumn>
                  <FlexGridColumn
                    binding="CONTRACT_NUMBER"
                    header="契約番号"
                    align="right"
                    width={82}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="KEI_DATE" header="契約日" width={140}></FlexGridColumn>
                  <FlexGridColumn binding="NAME_1" header="御名前" width={180}></FlexGridColumn>
                  <FlexGridColumn
                    binding="SALES_TAN_CD"
                    header="営業"
                    width={80}
                    allowResizing={false}
                    dataMap={planerMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="GENBA_TAN_CD"
                    header="現場担当"
                    allowResizing={false}
                    dataMap={planerMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="PROJECT_TYPE"
                    header="区分①"
                    width={60}
                    allowResizing={false}
                    dataMap={projectTypesMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="JINKOU"
                    header="人工"
                    width={80}
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="OLD_TAN_CD"
                    header="旧担当"
                    allowResizing={false}
                    dataMap={planerMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="FULL_ADDRESS"
                    header="ご住所・工事場所"
                    width={200}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="KOUJI_MEI" header="工事名"></FlexGridColumn>
                  <FlexGridColumn
                    binding="ROBOT_TYPE"
                    dataMap={robotTypesMap}
                    header="ﾛﾎﾞｯﾄ工事種類"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="ROBOT_MAKER" header="ﾛﾎﾞｯﾄ機種名"></FlexGridColumn>
                  <FlexGridColumn
                    binding="ZEINUKI_KIN"
                    header="工事金額(税抜)"
                    width={120}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="TAN_MEETING_FLG_DISP"
                    header="担当打合"
                    allowResizing={false}
                    align="center"
                    width={80}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="GREETING_FLG_DISP"
                    header="挨拶連絡"
                    allowResizing={false}
                    align="center"
                    width={80}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KOUKI_ST"
                    header="着工(作業)予定日"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="WORK_SCHEDULED_DATE"
                    header="作業日連絡"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KOUKI_ED"
                    header="完成予定日"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="HIKI_DATE"
                    header="引渡日"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="INSPECTION_FLG_DISP"
                    header="アフター実施"
                    width={100}
                    align="center"
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="CONSTRUCTION_SCHEDULE_BIKO"
                    header="備考"
                    width={200}
                  ></FlexGridColumn>
                </FlexGrid>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {/** 工事状況モーダル */}
      <ConstructionScheduleEditModal params={constructionScheduleEditModalParam} />
    </>
  );
};

export default ConstructionScheduleList;
